import { t } from "@travily/i18n";
import { Alert } from "@ui/components/Alert";
import { TileRadioGroup } from "@ui/components/TileRadioGroup/TileRadioGroup";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ListingFieldCopy, ListingType } from "./types";

const COLOR = "green" as const;
type SelectionSize = "3xl" | "4xl" | "5xl";

// i18n-tasks-use t('web.listings.fields.price_type.product.heading')
// i18n-tasks-use t('web.listings.fields.price_type.product.helper')
// i18n-tasks-use t('web.listings.fields.price_type.service.heading')
// i18n-tasks-use t('web.listings.fields.price_type.service.helper')

export function priceTypeFieldCopy(listingType: ListingType): ListingFieldCopy {
  return {
    heading: t(`web.listings.fields.price_type.${listingType}.heading`),
    helper: t(`web.listings.fields.price_type.${listingType}.helper`),
  };
}

export interface PriceTypeFieldProps {
  listingType: ListingType;
  onChange: (value: string) => void;
  /**
   * Whether the seller can take payment. A paid listing on an account without
   * payouts connected drops out of the marketplace, so the switch says so
   * rather than delisting silently.
   */
  stripeConnected?: boolean;
  size?: SelectionSize;
  value: "free" | "paid";
}

export function PriceTypeField({ listingType, onChange, size, stripeConnected, value }: PriceTypeFieldProps) {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize: SelectionSize = size ?? (isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl");
  const { heading } = priceTypeFieldCopy(listingType);

  return (
    <>
      <TileRadioGroup
        align="horizontal"
        aria-label={heading}
        color={COLOR}
        onChange={onChange}
        size={selectionGroupSize}
        value={value}
      >
        <TileRadioGroup.Item value="free">{t("web.listings.fields.price_type.free_label")}</TileRadioGroup.Item>
        <TileRadioGroup.Item value="paid">{t("web.listings.fields.price_type.paid_label")}</TileRadioGroup.Item>
      </TileRadioGroup>

      {value === "paid" && stripeConnected === false ? (
        <div className="mt-6">
          <Alert
            description={t("web.listings.fields.price_type.payouts_required_description")}
            size="sm"
            status="warning"
            title={t("web.listings.fields.price_type.payouts_required_title")}
          />
        </div>
      ) : null}
    </>
  );
}
